import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { JSONContent } from '@tiptap/react';
import type {
  CardCreate,
  CardFlag,
  CardState,
  CardType,
  DeckConfigUpdate,
  McqExtra,
  ReviewRating,
} from '@yorukiri/shared';
import { api } from './api';

// Summary used by list endpoints — no doc content, plus a hasChildren flag so
// the sidebar knows whether to render an expand chevron without a separate call.
export type ApiPageSummary = {
  id: string;
  userId: string;
  parentPageId: string | null;
  title: string;
  subtitle: string | null;
  color: string | null;
  position: number;
  createdAt: string;
  updatedAt: string;
  hasChildren: boolean;
};

export type ApiPage = ApiPageSummary & { content: JSONContent };

export type ApiPageWithAccess = {
  page: ApiPage;
  access: { role: 'owner' | 'editor' | 'viewer'; source: 'owner' | 'member' | 'link' };
};

export type ApiCard = {
  id: string;
  pageId: string;
  createdBy: string;
  type: CardType;
  front: string;
  back: string;
  extra: McqExtra | Record<string, unknown> | null;
  noteId: string | null;
  createdAt: string;
  updatedAt: string;
  state: CardState | null;
  stepIndex: number | null;
  intervalDays: number | null;
  ease: number | null;
  stability: number | null;
  difficulty: number | null;
  lapses: number | null;
  reps: number | null;
  flag: CardFlag | null;
  suspended: string | null;
  buriedUntil: string | null;
  lastReviewedAt: string | null;
  dueAt: string | null;
};

// Due-endpoint rows carry the parent page title so the review session can label
// cards without a separate /pages lookup.
export type ApiDueCard = ApiCard & { pageTitle: string };

export type ApiDeckConfig = {
  rootPageId: string;
  maxNewPerDay: number;
  maxReviewsPerDay: number;
  learningStepsMinutes: number[];
  relearningStepsMinutes: number[];
  graduatingIntervalDays: number;
  easyIntervalDays: number;
  leechThreshold: number;
  leechAction: 'suspend' | 'tag';
  buryNoteSiblings: boolean;
  scheduler: 'fsrs' | 'sm2';
  fsrsRetention: number;
};

export type RateCardResponse = {
  ok: true;
  reviewId: string;
  state: CardState;
  nextDueAt: string;
  isLeech: boolean;
  suspended: boolean;
};

export type ApiReviewSummary = {
  totalDue: number;
  totalLearning: number;
  totalNew: number;
  studied30d: number;
  streak: number;
  byPage: {
    pageId: string;
    title: string;
    color: string | null;
    due: number;
    learning: number;
    new: number;
    suspended: number;
    buried: number;
    total: number;
  }[];
  recentSessions: {
    day: string;
    pageId: string;
    pageTitle: string;
    reviewed: number;
    again: number;
  }[];
  forecast: { day: string; count: number }[];
  retention30d: number;
  matureCount: number;
  youngCount: number;
  daily30d: { day: string; reviewed: number; again: number }[];
  perDeckStats: {
    pageId: string;
    title: string;
    retention: number | null;
    reviews: number;
    mature: number;
    young: number;
  }[];
  leeches: {
    cardId: string;
    pageId: string;
    pageTitle: string;
    type: CardType;
    front: string;
    lapses: number;
  }[];
};

export type ApiCardHistory = {
  card: {
    id: string;
    pageId: string;
    type: CardType;
    front: string;
    back: string;
  };
  state: {
    state: CardState;
    intervalDays: number;
    ease: number;
    stability: number;
    difficulty: number;
    lapses: number;
    reps: number;
    flag: CardFlag | null;
    suspended: string | null;
    dueAt: string;
    lastReviewedAt: string | null;
  } | null;
  history: {
    id: string;
    rating: ReviewRating;
    reviewedAt: string;
    elapsedMs: number | null;
    intervalDays: number;
    ease: number;
    stability: number;
    difficulty: number;
    state: CardState;
    undone: string | null;
  }[];
};

export function useCardHistory(cardId: string | undefined) {
  return useQuery({
    queryKey: cardId ? ['cards', 'history', cardId] : ['cards', 'history', 'none'],
    queryFn: () => api.get<ApiCardHistory>(`/cards/${cardId}/history`),
    enabled: Boolean(cardId),
  });
}

const keys = {
  pages: ['pages'] as const,
  page: (id: string) => ['pages', id] as const,
  // 'root' is used as a sentinel since React Query keys don't serialize null distinctively.
  children: (parentId: string | null) => ['pages', 'children', parentId ?? 'root'] as const,
  cards: (pageId: string, opts?: { descendants?: boolean; due?: boolean }) =>
    ['cards', pageId, opts?.descendants ?? false, opts?.due ?? false] as const,
};

// Full flat list (summaries only) — used by library grid.
export function usePages() {
  return useQuery({
    queryKey: keys.pages,
    queryFn: () => api.get<ApiPageSummary[]>('/pages'),
  });
}

// Direct children of a single parent — used by the lazy sidebar tree.
// parentId === null means root-level pages.
export function usePageChildren(parentId: string | null, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: keys.children(parentId),
    queryFn: () =>
      api.get<ApiPageSummary[]>(`/pages?parent=${parentId === null ? 'root' : parentId}`),
    enabled: opts?.enabled ?? true,
  });
}

// Parallel fetch of multiple parents' children — used by PageTree for its set
// of expanded branches.
export function useExpandedChildren(parentIds: string[]) {
  return useQueries({
    queries: parentIds.map((id) => ({
      queryKey: keys.children(id),
      queryFn: () => api.get<ApiPageSummary[]>(`/pages?parent=${id}`),
    })),
  });
}

export function usePage(id: string | undefined) {
  return useQuery({
    queryKey: id ? keys.page(id) : ['pages', 'none'],
    queryFn: () => api.get<ApiPageWithAccess>(`/pages/${id}`),
    enabled: Boolean(id),
  });
}

function invalidatePageLists(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: keys.pages });
  qc.invalidateQueries({ queryKey: ['pages', 'children'] });
}

export function useCreatePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { title?: string; parentPageId?: string | null }) =>
      api.post<ApiPage>('/pages', {
        title: input.title ?? 'Untitled',
        parentPageId: input.parentPageId ?? null,
      }),
    onSuccess: () => invalidatePageLists(qc),
  });
}

export function useSavePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      title?: string;
      subtitle?: string | null;
      content?: JSONContent;
      color?: string | null;
    }) => {
      const { id, ...body } = input;
      return api.patch<ApiPage>(`/pages/${id}`, body);
    },
    onSuccess: (page) => {
      qc.setQueryData(keys.page(page.id), (prev: ApiPageWithAccess | undefined) =>
        prev ? { ...prev, page } : prev,
      );
      invalidatePageLists(qc);
    },
  });
}

export function useMovePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; parentPageId: string | null; position: number }) =>
      api.patch<ApiPage>(`/pages/${input.id}`, {
        parentPageId: input.parentPageId,
        position: input.position,
      }),
    // Optimistic update across all loaded children lists: remove from old parent,
    // insert into the destination. If the destination list isn't loaded yet we
    // skip the insert — the invalidation on settle will fetch it fresh.
    onMutate: async ({ id, parentPageId, position }) => {
      await qc.cancelQueries({ queryKey: ['pages', 'children'] });
      const snapshots: [readonly unknown[], ApiPageSummary[] | undefined][] = [];
      let movedPage: ApiPageSummary | null = null;

      const allChildrenQueries = qc.getQueriesData<ApiPageSummary[]>({
        queryKey: ['pages', 'children'],
      });
      for (const [key, data] of allChildrenQueries) {
        snapshots.push([key, data]);
        if (!Array.isArray(data)) continue;
        const found = data.find((p) => p.id === id);
        if (found) movedPage = found;
      }

      const destKey = parentPageId ?? 'root';
      for (const [key, data] of allChildrenQueries) {
        if (!Array.isArray(data)) continue;
        const parentKey = (key as readonly unknown[])[2];
        let next = data.filter((p) => p.id !== id);
        if (parentKey === destKey && movedPage) {
          const updated = { ...movedPage, parentPageId, position };
          next = [...next, updated].sort((a, b) => a.position - b.position);
        }
        qc.setQueryData(key, next);
      }
      return { snapshots };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.snapshots) {
        for (const [key, data] of ctx.snapshots) qc.setQueryData(key, data);
      }
    },
    onSettled: () => invalidatePageLists(qc),
  });
}

export function useDeletePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/pages/${id}`),
    onSuccess: () => invalidatePageLists(qc),
  });
}

export function useCards(
  pageId: string | undefined,
  opts?: { descendants?: boolean; due?: boolean },
) {
  const params = new URLSearchParams();
  if (pageId) params.set('pageId', pageId);
  if (opts?.descendants) params.set('descendants', '1');
  if (opts?.due) params.set('due', '1');
  return useQuery({
    queryKey: pageId ? keys.cards(pageId, opts) : ['cards', 'none'],
    queryFn: () => api.get<ApiCard[]>(`/cards?${params.toString()}`),
    enabled: Boolean(pageId),
  });
}

export function useCreateCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CardCreate) => api.post<ApiCard>('/cards', input),
    onSuccess: (_card, input) => {
      qc.invalidateQueries({ queryKey: ['cards', input.pageId] });
    },
  });
}

// Cross-page due queue for "review everything". Descendants-aware review of a
// single page still goes through useCards(pageId, { descendants: true, due: true }).
export function useDueCards() {
  return useQuery({
    queryKey: ['cards', 'due'],
    queryFn: () => api.get<ApiDueCard[]>('/cards/due'),
  });
}

export type ExtraMode = 'ahead' | 'cram' | 'new' | 'failed';

export function useExtraCards(opts: {
  pageId?: string;
  mode: ExtraMode;
  days?: number;
  limit?: number;
  descendants?: boolean;
  enabled?: boolean;
}) {
  const params = new URLSearchParams();
  params.set('mode', opts.mode);
  if (opts.pageId) params.set('pageId', opts.pageId);
  if (opts.days) params.set('days', String(opts.days));
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.descendants) params.set('descendants', '1');
  return useQuery({
    queryKey: ['cards', 'extra', opts.pageId ?? 'all', opts.mode, opts.days ?? 0, opts.limit ?? 0],
    queryFn: () => api.get<ApiDueCard[]>(`/cards/extra?${params.toString()}`),
    enabled: opts.enabled ?? true,
  });
}

export function useReviewSummary() {
  return useQuery({
    queryKey: ['reviews', 'summary'],
    queryFn: () => api.get<ApiReviewSummary>('/cards/summary'),
  });
}

export function useRateCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { cardId: string; rating: ReviewRating; elapsedMs?: number }) =>
      api.post<RateCardResponse>('/cards/review', input),
    onSuccess: () => {
      // Any cached card list could be showing stale due-counts; invalidate broadly.
      qc.invalidateQueries({ queryKey: ['cards'] });
      qc.invalidateQueries({ queryKey: ['reviews', 'summary'] });
    },
  });
}

export function useUndoReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { reviewId: string }) =>
      api.post<{ ok: true }>('/cards/review/undo', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cards'] });
      qc.invalidateQueries({ queryKey: ['reviews', 'summary'] });
    },
  });
}

export function useCardAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      cardId: string;
      action: 'suspend' | 'unsuspend' | 'bury' | 'unbury';
    }) => api.post<{ ok: true }>('/cards/action', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cards'] });
      qc.invalidateQueries({ queryKey: ['reviews', 'summary'] });
    },
  });
}

export function useCardFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { cardId: string; flag: CardFlag | null }) =>
      api.post<{ ok: true }>('/cards/flag', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cards'] }),
  });
}

export function useDeckConfig(pageId: string | undefined) {
  return useQuery({
    queryKey: pageId ? ['deckConfig', pageId] : ['deckConfig', 'none'],
    queryFn: () => api.get<ApiDeckConfig>(`/cards/config/${pageId}`),
    enabled: Boolean(pageId),
  });
}

export function useUpdateDeckConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { pageId: string; patch: DeckConfigUpdate }) =>
      api.patch<{ ok: true; rootPageId: string }>(`/cards/config/${input.pageId}`, input.patch),
    onSuccess: (_data, input) => {
      qc.invalidateQueries({ queryKey: ['deckConfig', input.pageId] });
      qc.invalidateQueries({ queryKey: ['cards'] });
    },
  });
}

export type ApiPlan = {
  productId: string;
  name: string;
  cadence: 'monthly' | 'annual';
  creditsPerCycle: number;
};

export type ApiBillingMe = {
  credits: number;
  dodoCustomerId: string | null;
};

export type ApiSubscription =
  | ({
      id: string;
      userId: string;
      status: string;
      productId: string;
      currentPeriodEnd: string | null;
    } & Record<string, unknown>)
  | null;

export function useBillingPlans() {
  return useQuery({
    queryKey: ['billing', 'plans'],
    queryFn: () => api.get<ApiPlan[]>('/billing/plans'),
  });
}

export function useBillingMe() {
  return useQuery({
    queryKey: ['billing', 'me'],
    queryFn: () => api.get<ApiBillingMe>('/billing/me'),
  });
}

export function useSubscription() {
  return useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: () => api.get<ApiSubscription>('/billing/subscription'),
  });
}

export function useCheckout() {
  return useMutation({
    mutationFn: (input: { productId: string; returnUrl?: string; cancelUrl?: string }) =>
      api.post<{ url: string }>('/billing/checkout', input),
  });
}
